// PayrollDates.jsx

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  CircularProgress,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Chip,
  Divider,
  Stack,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { 
  CalendarToday, 
  Warning, 
  CheckCircle, 
  Schedule,
  AccessTime,
  Edit
} from "@mui/icons-material";

// Service
import employerSvc from "../../../services/employer/employerSvc";

// Function to parse date string and convert to Date object
const parseDate = (dateStr) => {
  return new Date(dateStr);
};

// Function to check if a string is a valid date
const isValidDate = (dateStr) => {
  const date = new Date(dateStr);
  return date instanceof Date && !isNaN(date) && dateStr.trim() !== '';
};

// Function to check if payroll data contains actual dates
const hasValidDates = (payrollData) => {
  if (!Array.isArray(payrollData) || payrollData.length === 0) {
    return false;
  }
  
  return payrollData.some(item => {
    return Object.keys(item).some(cutoffDate => {
      return isValidDate(cutoffDate);
    });
  });
};

// Function to find the next upcoming deadline
const findNextDeadline = (data) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison
  
  let nextDeadline = null;
  let minDaysDiff = Infinity;

  data.forEach((item) => {
    Object.keys(item).forEach((cutoffDate) => {
      const deadlineDate = parseDate(cutoffDate);
      const daysDiff = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));
      
      // If deadline is today or in the future and closer than current min
      if (daysDiff >= 0 && daysDiff < minDaysDiff) {
        minDaysDiff = daysDiff;
        nextDeadline = {
          cutoffDate,
          effectiveDate: item[cutoffDate],
          daysUntil: daysDiff
        };
      }
    });
  });

  return nextDeadline;
};

function PayrollDates() {
  const { employerId, planId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [employerName, setEmployerName] = useState("");
  const [payrollTitle, setPayrollTitle] = useState("Payroll Cut-Off Dates");
  const [payrollData, setPayrollData] = useState([]);
  const [todayDate, setTodayDate] = useState("");
  const [nextDeadline, setNextDeadline] = useState(null);
  const [reliusWeb, setReliusWeb] = useState("retirement.tcgservices.com");
  const [hasValidDateData, setHasValidDateData] = useState(false);

  // Function to handle redirect to relius_web
  const handleMakeChanges = () => {
    const finalReliusWeb = reliusWeb || "retirement.tcgservices.com";
    window.open(`https://${finalReliusWeb}`, '_blank');
  };

  // Function to get today's date in CST
  const getTodayDateCST = () => {
    const now = new Date();
    const cstOptions = {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    };
    return now.toLocaleDateString('en-US', cstOptions);
  };

  // Function to get row styling based on date status
  const getRowStyle = (cutoffDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = parseDate(cutoffDate);
    const daysDiff = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) {
      // Past deadline
      return { 
        backgroundColor: '#fafafa', 
        color: '#666',
        '&:hover': { backgroundColor: '#f0f0f0' }
      };
    } else if (daysDiff === 0) {
      // Today's deadline
      return { 
        backgroundColor: '#fff8e1', 
        fontWeight: 'bold',
        borderLeft: '4px solid #ff9800',
        '&:hover': { backgroundColor: '#fff3cd' }
      };
    } else if (daysDiff <= 7) {
      // Upcoming deadline (within 7 days)
      return { 
        backgroundColor: '#e3f2fd', 
        fontWeight: 'bold',
        borderLeft: '4px solid #2196f3',
        '&:hover': { backgroundColor: '#bbdefb' }
      };
    } else {
      // Future deadline
      return {
        '&:hover': { backgroundColor: '#f5f5f5' }
      };
    }
  };

  // Function to get status chip for a date
  const getStatusChip = (cutoffDate) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const deadlineDate = parseDate(cutoffDate);
    const daysDiff = Math.ceil((deadlineDate - today) / (1000 * 60 * 60 * 24));

    if (daysDiff < 0) {
      return (
        <Chip 
          icon={<CheckCircle />}
          label="Completed" 
          size="small" 
          color="default" 
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    } else if (daysDiff === 0) {
      return (
        <Chip 
          icon={<Warning />}
          label="Due Today!" 
          size="small" 
          color="warning"
          sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
        />
      );
    } else if (daysDiff <= 7) {
      return (
        <Chip 
          icon={<AccessTime />}
          label={`${daysDiff} day${daysDiff === 1 ? '' : 's'}`} 
          size="small" 
          color="info"
          sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
        />
      );
    } else {
      return (
        <Chip 
          icon={<Schedule />}
          label="Upcoming" 
          size="small" 
          color="success" 
          variant="outlined"
          sx={{ fontSize: '0.75rem' }}
        />
      );
    }
  };

  useEffect(() => {
    const fetchPayrollInfo = async () => {
      try {
        setLoading(true);

        // Set today's date
        setTodayDate(getTodayDateCST());

        // 1) Fetch employer details (includes websites array with relius_web)
        const { employerInfo, payroll_dates, websites } =
          await employerSvc.getEmployerDetails({
            employerIdForURL: employerId,
            page: "formSearch",
          });

        // 2) Employer name
        setEmployerName(employerInfo?.employerName || "");

        // 3) Get relius_web from websites array
        const foundRelius = websites?.[0]?.relius_web || "retirement.tcgservices.com";
        setReliusWeb(foundRelius);

        // 4) Derive plan code (e.g. "403b") from "403b-xxxx"
        const planCode = planId.split("-")[0];

        // 4) Instead of assuming payroll_dates[0], iterate to find the matching item
        //    We'll search each element's `plans` array for a plan with PayrollDatesPlanDesc === planCode
        if (Array.isArray(payroll_dates)) {
          // For each payroll_dates element, check if it has plans that match
          const matchedEntry = payroll_dates.find((entry) =>
            entry.plans?.some((p) => p.PayrollDatesPlanDesc === planCode)
          );

          if (matchedEntry) {
            // Find the actual plan inside that entry
            const matchedPlan = matchedEntry.plans.find(
              (p) => p.PayrollDatesPlanDesc === planCode
            );
            // If the plan has a friendly display name
            if (matchedPlan?.PayrollDatesDisplayName) {
              setPayrollTitle(matchedPlan.PayrollDatesDisplayName);
            }

            // The cut-off dates are in matchedEntry.payrollData
            if (matchedEntry.payrollData) {
              setPayrollData(matchedEntry.payrollData);
              // Check if payroll data contains valid dates
              const hasDates = hasValidDates(matchedEntry.payrollData);
              setHasValidDateData(hasDates);
              
              // Only find next deadline if we have valid dates
              if (hasDates) {
                const next = findNextDeadline(matchedEntry.payrollData);
                setNextDeadline(next);
              } else {
                setNextDeadline(null);
              }
            }
          }
        }
      } catch (error) {
        console.error("Error loading payroll info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPayrollInfo();
  }, [employerId, planId]);

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          height: "50vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Loading payroll information...
          </Typography>
        </Box>
      </Container>
    );
  }

  // ---------- MOBILE (SM and below) ----------
  if (isMobile) {
    return (
      <Container component="main" maxWidth="sm" sx={{ py: 2 }}>
        {/* Modern Header */}
        <Box sx={{ mb: 4 }}>
          {/* Main Header Card */}
          <Card 
            sx={{ 
              background: 'linear-gradient(135deg, #183361 0%, #1e4a7a 50%, #2c5aa0 100%)',
              color: 'white',
              borderRadius: 4,
              boxShadow: '0 20px 40px rgba(24, 51, 97, 0.3)',
              overflow: 'hidden',
              position: 'relative',
              mb: 2
            }}
          >
            {/* Animated Background */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)',
                animation: 'pulse 4s ease-in-out infinite alternate',
                '@keyframes pulse': {
                  '0%': { opacity: 0.5 },
                  '100%': { opacity: 1 }
                }
              }}
            />
            
            <CardContent sx={{ p: 4, position: 'relative' }}>
              <Stack spacing={3} alignItems="center">
                {/* Employer Name */}
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: "800", 
                    textAlign: 'center',
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    fontSize: '2rem',
                    letterSpacing: '-0.5px'
                  }}
                >
                  {employerName}
                </Typography>
                
                {/* Plan Description */}
                <Typography 
                  variant="body1" 
                  sx={{ 
                    opacity: 0.9, 
                    textAlign: 'center',
                    lineHeight: 1.5,
                    fontSize: '0.95rem',
                    maxWidth: '320px'
                  }}
                >
                  {payrollTitle}
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Info Cards Row */}
          <Grid container spacing={2}>
            {/* Today's Date Card */}
            <Grid item xs={hasValidDateData ? 6 : 12}>
              <Card 
                sx={{ 
                  background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                  color: 'white',
                  borderRadius: 3,
                  boxShadow: '0 8px 25px rgba(74, 144, 226, 0.3)',
                  height: '100%'
                }}
              >
                <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                  <CalendarToday sx={{ fontSize: 24, mb: 1, opacity: 0.9 }} />
                  <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '0.8rem', mb: 0.5 }}>
                    Today
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: '500', fontSize: '0.75rem', opacity: 0.9 }}>
                    {todayDate.split(',')[0]}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                    {todayDate.split(',')[1]}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Next Deadline Card - only show if we have valid date data */}
            {hasValidDateData && (
              <Grid item xs={6}>
                <Card 
                  sx={{ 
                    background: nextDeadline?.daysUntil <= 3 
                      ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                      : nextDeadline?.daysUntil <= 7
                      ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                      : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                    color: 'white',
                    borderRadius: 3,
                    boxShadow: nextDeadline?.daysUntil <= 3 
                      ? '0 8px 25px rgba(231, 76, 60, 0.3)'
                      : nextDeadline?.daysUntil <= 7
                      ? '0 8px 25px rgba(243, 156, 18, 0.3)'
                      : '0 8px 25px rgba(52, 152, 219, 0.3)',
                    height: '100%'
                  }}
                >
                  <CardContent sx={{ p: 2.5, textAlign: 'center' }}>
                    <AccessTime sx={{ fontSize: 24, mb: 1, opacity: 0.9 }} />
                    <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '0.8rem', mb: 0.5 }}>
                      Next Deadline
                    </Typography>
                    {nextDeadline ? (
                      <>
                        <Typography variant="body2" sx={{ fontWeight: '500', fontSize: '0.75rem', opacity: 0.9 }}>
                          {nextDeadline.cutoffDate}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                          {nextDeadline.daysUntil === 0 
                            ? 'Due today!' 
                            : `${nextDeadline.daysUntil} day${nextDeadline.daysUntil === 1 ? '' : 's'}`
                          }
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                        No upcoming deadlines
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Payroll Dates Table */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#183361' }}>
                {hasValidDateData ? 'Payroll Deadlines' : 'Payroll Information'}
              </Typography>
            </Box>
            
            {payrollData.length > 0 ? (
              <TableContainer>
                <Table size="small" aria-label="Payroll Dates">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                      {hasValidDateData ? (
                        <>
                          <TableCell sx={{ fontWeight: "bold", fontSize: '0.875rem' }}>
                            Submit By
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", fontSize: '0.875rem' }}>
                            Effective Date
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", fontSize: '0.875rem' }}>
                            Status
                          </TableCell>
                        </>
                      ) : (
                        <TableCell sx={{ fontWeight: "bold", fontSize: '0.875rem' }}>
                          Information
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {payrollData.map((item, idx) =>
                      Object.keys(item).map((cutoffDate) => (
                        <TableRow 
                          key={`${cutoffDate}-${idx}`}
                          sx={hasValidDateData ? getRowStyle(cutoffDate) : {}}
                        >
                          {hasValidDateData ? (
                            <>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {cutoffDate}
                              </TableCell>
                              <TableCell sx={{ fontSize: '0.875rem' }}>
                                {item[cutoffDate]}
                              </TableCell>
                              <TableCell>
                                {getStatusChip(cutoffDate)}
                              </TableCell>
                            </>
                          ) : (
                            <TableCell sx={{ fontSize: '0.875rem', py: 2 }}>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                {cutoffDate}
                              </Typography>
                              <Typography variant="body2">
                                {item[cutoffDate]}
                              </Typography>
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No payroll information available for this plan.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Make Changes Button */}
        <Card sx={{ borderRadius: 3, boxShadow: 3, mb: 3 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: "#183361", mb: 2, fontWeight: 'bold' }}>
              Ready to Make Changes?
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, lineHeight: 1.5 }}>
              Submit your enrollment changes online.
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Edit />}
              onClick={handleMakeChanges}
              sx={{
                background: 'linear-gradient(135deg, #183361 0%, #2c5aa0 100%)',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
                py: 1.5,
                px: 4,
                borderRadius: 3,
                boxShadow: '0 8px 25px rgba(24, 51, 97, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1e4a7a 0%, #183361 100%)',
                  boxShadow: '0 12px 35px rgba(24, 51, 97, 0.4)',
                  transform: 'translateY(-2px)'
                },
                transition: 'all 0.3s ease-in-out'
              }}
            >
              Make Changes Now
            </Button>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: "#183361", mb: 2, fontWeight: 'bold' }}>
              Need Help?
            </Typography>
            <Typography variant="body1" sx={{ color: "#183361", mb: 2 }}>
              Contact TCG Administrators at
            </Typography>
            <Typography variant="h6" sx={{ color: "#183361", fontWeight: 'bold', mb: 2 }}>
              1&#8209;800&#8209;943&#8209;9179
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", lineHeight: 1.5 }}
            >
              This document is designed to inform Participants about the Plan in
              non-technical language. Every attempt is made to convey the Plan
              accurately. If anything in this Summary Plan Description varies from
              the Plan Documents, the Plan Documents govern.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  // ---------- DESKTOP (MD and up) ----------
  return (
    <Container component="main" maxWidth="lg" sx={{ py: 4 }}>
      {/* Modern Header Section */}
      <Box sx={{ mb: 5 }}>
        {/* Main Header */}
        <Card 
          sx={{ 
            background: 'linear-gradient(135deg, #183361 0%, #1e4a7a 50%, #2c5aa0 100%)',
            color: 'white',
            borderRadius: 4,
            boxShadow: '0 25px 50px rgba(24, 51, 97, 0.4)',
            overflow: 'hidden',
            position: 'relative',
            mb: 3
          }}
        >
          {/* Animated Background */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 70%, rgba(255,255,255,0.1) 0%, transparent 60%), radial-gradient(circle at 70% 30%, rgba(255,255,255,0.05) 0%, transparent 60%)',
              animation: 'pulse 6s ease-in-out infinite alternate',
              '@keyframes pulse': {
                '0%': { opacity: 0.3 },
                '100%': { opacity: 0.8 }
              }
            }}
          />
          
          <CardContent sx={{ p: 5, position: 'relative' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: "800", 
                  mb: 2,
                  textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                  fontSize: { md: '3rem', lg: '3.5rem' },
                  letterSpacing: '-1px',
                  lineHeight: 1.1
                }}
              >
                {employerName}
              </Typography>
              <Typography 
                variant="h5" 
                sx={{ 
                  opacity: 0.9, 
                  lineHeight: 1.4,
                  maxWidth: '600px',
                  mx: 'auto',
                  fontSize: { md: '1.25rem', lg: '1.5rem' },
                  fontWeight: '400'
                }}
              >
                {payrollTitle}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Info Cards Row */}
        <Grid container spacing={3}>
          {/* Today's Date Card */}
          <Grid item xs={12} md={hasValidDateData ? 6 : 12}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)',
                color: 'white',
                borderRadius: 4,
                boxShadow: '0 15px 35px rgba(74, 144, 226, 0.3)',
                height: '100%',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <CalendarToday sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                <Typography variant="h6" sx={{ fontWeight: '700', mb: 1 }}>
                  Today's Date
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: '600', mb: 0.5 }}>
                  {todayDate.split(',')[0]}
                </Typography>
                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                  {todayDate.split(',')[1]}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Next Deadline Card - only show if we have valid date data */}
          {hasValidDateData && (
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  background: nextDeadline?.daysUntil <= 3 
                    ? 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)'
                    : nextDeadline?.daysUntil <= 7
                    ? 'linear-gradient(135deg, #f39c12 0%, #e67e22 100%)'
                    : 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                  color: 'white',
                  borderRadius: 4,
                  boxShadow: nextDeadline?.daysUntil <= 3 
                    ? '0 15px 35px rgba(231, 76, 60, 0.3)'
                    : nextDeadline?.daysUntil <= 7
                    ? '0 15px 35px rgba(243, 156, 18, 0.3)'
                    : '0 15px 35px rgba(52, 152, 219, 0.3)',
                  height: '100%',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <AccessTime sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography variant="h6" sx={{ fontWeight: '700', mb: 1 }}>
                    Next Deadline
                  </Typography>
                  {nextDeadline ? (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: '600', mb: 0.5 }}>
                        {nextDeadline.cutoffDate}
                      </Typography>
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {nextDeadline.daysUntil === 0 
                          ? 'Due today!' 
                          : `${nextDeadline.daysUntil} day${nextDeadline.daysUntil === 1 ? '' : 's'} remaining`
                        }
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body1" sx={{ opacity: 0.9 }}>
                      No upcoming deadlines
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Main Content Grid */}
      <Grid container spacing={4}>
        {/* Payroll Dates Table */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#183361' }}>
                  {hasValidDateData ? 'Payroll Deadlines' : 'Payroll Information'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {hasValidDateData 
                    ? 'Submit your changes by the dates below to ensure they take effect on the corresponding paycheck.'
                    : 'Important information about payroll deadlines and submission requirements.'
                  }
                </Typography>
              </Box>
              
              {payrollData.length > 0 ? (
                <TableContainer>
                  <Table aria-label="Payroll Dates">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                        {hasValidDateData ? (
                          <>
                            <TableCell sx={{ fontWeight: "bold", fontSize: '1rem', py: 2 }}>
                              Changes Submitted Online By
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: '1rem', py: 2 }}>
                              Effective Paycheck Date
                            </TableCell>
                            <TableCell sx={{ fontWeight: "bold", fontSize: '1rem', py: 2 }}>
                              Status
                            </TableCell>
                          </>
                        ) : (
                          <TableCell sx={{ fontWeight: "bold", fontSize: '1rem', py: 2 }}>
                            Information
                          </TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payrollData.map((item, idx) =>
                        Object.keys(item).map((cutoffDate) => (
                          <TableRow 
                            key={`${cutoffDate}-${idx}`}
                            sx={hasValidDateData ? getRowStyle(cutoffDate) : {}}
                          >
                            {hasValidDateData ? (
                              <>
                                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                                  {cutoffDate}
                                </TableCell>
                                <TableCell sx={{ fontSize: '1rem', py: 2 }}>
                                  {item[cutoffDate]}
                                </TableCell>
                                <TableCell sx={{ py: 2 }}>
                                  {getStatusChip(cutoffDate)}
                                </TableCell>
                              </>
                            ) : (
                              <TableCell sx={{ fontSize: '1rem', py: 3 }}>
                                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1.5 }}>
                                  {cutoffDate}
                                </Typography>
                                <Typography variant="body1">
                                  {item[cutoffDate]}
                                </Typography>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No payroll information available for this plan.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Make Changes Button */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: "#183361", mb: 2, fontWeight: 'bold' }}>
                  Ready to Make Changes?
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mb: 3, lineHeight: 1.5 }}>
                  Submit your enrollment changes online.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Edit />}
                  onClick={handleMakeChanges}
                  sx={{
                    background: 'linear-gradient(135deg, #183361 0%, #2c5aa0 100%)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    py: 2,
                    px: 4,
                    borderRadius: 3,
                    boxShadow: '0 12px 35px rgba(24, 51, 97, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1e4a7a 0%, #183361 100%)',
                      boxShadow: '0 16px 45px rgba(24, 51, 97, 0.4)',
                      transform: 'translateY(-3px)'
                    },
                    transition: 'all 0.3s ease-in-out',
                    width: '100%'
                  }}
                >
                  Make Changes Now
                </Button>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ color: "#183361", mb: 2, fontWeight: 'bold' }}>
                  Need Assistance?
                </Typography>
                <Typography variant="body1" sx={{ color: "#183361", mb: 2 }}>
                  Contact TCG Administrators for support:
                </Typography>
                <Typography variant="h5" sx={{ color: "#183361", fontWeight: 'bold', mb: 3 }}>
                  1&#8209;800&#8209;943&#8209;9179
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", lineHeight: 1.6 }}
                >
                  This document is designed to inform Participants about the Plan in
                  non-technical language. Every attempt is made to convey the Plan
                  accurately. If anything in this Summary Plan Description varies from
                  the Plan Documents, the Plan Documents govern.
                </Typography>
              </CardContent>
            </Card>

            {/* Legend */}
            <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ color: "#183361", mb: 2, fontWeight: 'bold' }}>
                  Status Legend
                </Typography>
                <Stack spacing={1.5}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      icon={<CheckCircle />}
                      label="Completed" 
                      size="small" 
                      color="default" 
                      variant="outlined"
                    />
                    <Typography variant="body2">Past deadlines</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      icon={<Warning />}
                      label="Due Today!" 
                      size="small" 
                      color="warning"
                    />
                    <Typography variant="body2">Today's deadline</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      icon={<AccessTime />}
                      label="7 days" 
                      size="small" 
                      color="info"
                    />
                    <Typography variant="body2">Within 7 days</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      icon={<Schedule />}
                      label="Upcoming" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                    />
                    <Typography variant="body2">Future deadlines</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
}

export default PayrollDates;

